/*
  Addisuna product card behaviour. Markup: snippets/addisuna-product-card.liquid.

  1. Entrance: cards still below the fold fade up as they scroll into view.
  2. Add to cart: the card's form is submitted over fetch, the cart drawer (or
     notification) is refreshed through Dawn's own renderContents(), and the
     button morphs to a checkmark. If anything fails the form submits normally,
     which lands the shopper on /cart with the item added.

  DELEGATION RULE: the submit handler is bound to `document`, never to a card.
  Dawn's facets replace the collection grid wholesale, and the theme editor
  reloads sections, so anything bound per card would silently go offline.
*/
(() => {
  if (window.__addisunaProductCardBound) return;
  window.__addisunaProductCardBound = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const ADDED_MS = 2000;
  const DRAWER_DELAY_MS = 650;
  let observer = null;

  function reveal(card) {
    card.classList.remove('apc--pending');
    window.setTimeout(() => card.classList.remove('apc--entering'), 1100);
  }

  function scan(root) {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            reveal(entry.target);
          });
        },
        { rootMargin: '0px 0px -50px 0px' }
      );
    }

    const viewportBottom = window.innerHeight;
    (root || document).querySelectorAll('[data-apc]:not([data-apc-seen])').forEach((card) => {
      card.setAttribute('data-apc-seen', '');
      /* Only animate what the shopper has not seen yet. A card that is already
         on screen stays put, so nothing ever blinks out after first paint. */
      if (card.getBoundingClientRect().top < viewportBottom) return;

      const siblings = card.parentElement && card.parentElement.parentElement
        ? Array.from(card.parentElement.parentElement.children)
        : [];
      const index = Math.max(0, siblings.indexOf(card.parentElement));
      card.style.setProperty('--apc-delay', `${(index % 4) * 70}ms`);
      card.classList.add('apc--pending', 'apc--entering');
      observer.observe(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document));
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  function cartComponent() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    return cart && typeof cart.renderContents === 'function' && typeof cart.getSectionsToRender === 'function'
      ? cart
      : null;
  }

  function updateBubble(sections) {
    const html = sections && sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (!html || !bubble) return;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (source) bubble.innerHTML = source.innerHTML;
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest && event.target.closest('[data-apc-form]');
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[data-apc-add]');
    const card = form.closest('[data-apc]');
    if (!button || !card) return;
    if (button.getAttribute('aria-busy') === 'true' || button.classList.contains('is-added')) return;

    const error = card.querySelector('[data-apc-error]');
    const status = card.querySelector('[data-apc-status]');
    const cart = cartComponent();

    const body = new FormData(form);
    const sectionIds = cart ? cart.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'];
    body.append('sections', sectionIds.join(','));
    body.append('sections_url', window.location.pathname);

    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    let data;
    try {
      const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body,
      });
      data = await response.json();
    } catch (err) {
      /* Degrade, do not die: a normal form post still adds the item. */
      button.removeAttribute('aria-busy');
      form.submit();
      return;
    }

    button.removeAttribute('aria-busy');

    if (data.status) {
      if (error) {
        error.textContent = data.description || data.message || 'This could not be added to your cart. Please try again.';
        error.hidden = false;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, { source: 'addisuna-product-card', productVariantId: body.get('id'), errors: data.description, message: data.message });
      }
      return;
    }

    button.classList.add('is-added');
    if (status) status.textContent = `${form.dataset.apcName || 'Item'} added to your cart.`;

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'addisuna-product-card', productVariantId: body.get('id'), cartData: data });
    }

    if (cart && data.sections) {
      /* Let the checkmark land before the drawer slides over the page. */
      window.setTimeout(() => {
        cart.classList.remove('is-empty');
        cart.renderContents(data);
      }, reduceMotion.matches ? 0 : DRAWER_DELAY_MS);
    } else {
      updateBubble(data.sections);
    }

    window.setTimeout(() => {
      button.classList.remove('is-added');
      if (status) status.textContent = '';
    }, ADDED_MS);
  });
})();
